import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Card, Skeleton, Typography } from '@chilecompra/react-kit/components'

import FormattedCurrencyComponent from '../../../components/customs/FormattedCurrency.component'
import FormattedDateComponent from '../../../components/customs/FormattedDate.component'

import { CURRENCY_TRANSLATION, DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY } from '../../../config/settings/constants'

import { Divider, Grid } from '../DirectDealSummary.styles'

/**
 * The DirectDealSummary's details amounts.
 */
const DirectDealSummaryDetailAmount = props => {
  const { loading, sheet } = props

  const [calculations, setCalculations] = useState({
    subtotalAmount: 0,
    taxAmount: 0,
    totalUTM: 0
  })

  const calculateSubtotalAndTax = sheetData => {
    if (!sheetData) return { subtotalAmount: 0, taxAmount: 0 }

    const {
      netAmount = 0,
      dispatchAmount = 0,
      chargesAmount = 0,
      discountAmount = 0,
      taxCode,
      taxPercent = 0
    } = sheetData

    let taxSheet = 0
    const isHonorariumType =
      taxCode === DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY.HONORARIUM_BENEFIT_SII ||
      taxCode === DIRECT_DEAL_HONORARIUM_PERCENT_DICTIONARY.HONORARIUM

    const subtotalSheet = Math.max(netAmount + dispatchAmount + chargesAmount - discountAmount, 0)

    if (isHonorariumType && taxPercent < 100) {
      taxSheet = subtotalSheet / (1 - taxPercent / 100)
    } else {
      taxSheet = subtotalSheet * (taxPercent / 100)
    }

    return {
      subtotalAmount: subtotalSheet,
      taxAmount: taxSheet
    }
  }

  const calculateUtmAmount = sheetData => {
    if (!sheetData) return 0

    const { currency, totalAmount = 0, valueCurrencyFromPublish = 0, valueUTMFromPublish = 0 } = sheetData

    if (valueUTMFromPublish <= 0) return 0

    const currenciesNeedingConversion = [
      CURRENCY_TRANSLATION.CLF.cod,
      CURRENCY_TRANSLATION.USD.cod,
      CURRENCY_TRANSLATION.EUR.cod
    ]
    let totalAmountSheetClp = totalAmount

    if (currenciesNeedingConversion.includes(currency)) {
      totalAmountSheetClp = totalAmount * valueCurrencyFromPublish
    }

    return (totalAmountSheetClp / valueUTMFromPublish).toFixed(2)
  }

  useEffect(() => {
    if (!sheet) {
      setCalculations({ subtotalAmount: 0, taxAmount: 0, totalUTM: 0 })
      return
    }

    const { subtotalAmount, taxAmount } = calculateSubtotalAndTax(sheet)
    const totalUTM = calculateUtmAmount(sheet)

    setCalculations({
      subtotalAmount,
      taxAmount,
      totalUTM
    })
  }, [sheet])

  return (
    <Grid padding="16px 0 0">
      <Card padding="24px 16px" color="blank" enableBorder>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body1" fontWeight="bold">
              Resumen monto total
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" fontWeight="bold">
              Neto
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2" fontWeight="bold">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(sheet?.netAmount || 0)
                      : sheet?.netAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">Despacho</Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(sheet?.dispatchAmount || 0)
                      : sheet?.dispatchAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">Descuento</Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(sheet?.discountAmount || 0)
                      : sheet?.discountAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">Cargos</Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(sheet?.chargesAmount || 0)
                      : sheet?.chargesAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" fontWeight="bold">
              Subtotal
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2" fontWeight="bold">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(calculations.subtotalAmount || 0)
                      : calculations.subtotalAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">Impuestos</Typography>
            <Typography variant="body2" color="black3">
              {sheet?.tax}
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(calculations.taxAmount || 0)
                      : calculations.taxAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} margin="8px 0">
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" fontWeight="bold" color="primary">
              Monto total
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            {loading && <Skeleton width="70%" />}
            {!loading && (
              <Typography variant="body2" fontWeight="bold">
                <FormattedCurrencyComponent
                  amount={
                    sheet?.currency === CURRENCY_TRANSLATION.CLP.cod
                      ? parseInt(sheet?.totalAmount || 0)
                      : sheet?.totalAmount || 0
                  }
                  currency={sheet?.currency || ''}
                  includeSymbol
                />
              </Typography>
            )}
          </Grid>
          {(sheet?.currency === CURRENCY_TRANSLATION.CLF.cod ||
            sheet?.currency === CURRENCY_TRANSLATION.CLP.cod ||
            sheet?.currency === CURRENCY_TRANSLATION.EUR.cod ||
            sheet?.currency === CURRENCY_TRANSLATION.USD.cod) &&
            calculations.totalUTM > 0 && (
              <>
                <Grid item xs={12} textAlign="right">
                  {loading && <Skeleton width="70%" />}
                  {!loading && (
                    <Typography variant="body2" fontSize="14px" color="black3" tabIndex="0">
                      <FormattedCurrencyComponent amount={calculations.totalUTM || 0} currency="UTM" /> UTM al día{' '}
                      <FormattedDateComponent date={sheet?.datePublish} />
                    </Typography>
                  )}
                </Grid>
              </>
            )}
        </Grid>
      </Card>
    </Grid>
  )
}

DirectDealSummaryDetailAmount.propTypes = {
  sheet: PropTypes.shape({
    chargesAmount: PropTypes.number,
    currency: PropTypes.string,
    datePublish: PropTypes.string,
    discountAmount: PropTypes.number,
    dispatchAmount: PropTypes.number,
    netAmount: PropTypes.number,
    tax: PropTypes.string,
    taxCode: PropTypes.number,
    taxPercent: PropTypes.number,
    totalAmount: PropTypes.number,
    valueCurrencyFromPublish: PropTypes.number,
    valueUTMFromPublish: PropTypes.number
  }),
  loading: PropTypes.bool
}

export default DirectDealSummaryDetailAmount
